import { useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { STATUS_META, APPROVAL_META, statusLabel, formatSqYd } from "@/lib/statusUtils";
import { DIRECTION_LABEL } from "@/lib/portionUtils";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { AlertTriangle, CheckCircle2, Split, Upload, X } from "lucide-react";

const EMPTY_FORM = {
  customer_name: "",
  customer_mobile: "",
  customer_email: "",
  customer_address: "",
  id_reference: "",
  quoted_price: "",
  payment_mode: "Cash",
  amount_paid: "",
  notes: "",
  // Partial-plot only
  area_sqyd: "",
  boundaries: { north: "", south: "", east: "", west: "" },
  sketch_url: "",
};

export default function PlotDetailDialog({ plot, onClose, onBooked }) {
  const { user } = useAuth();
  const [mode, setMode] = useState("view"); // view | book | split
  const [selectedPortionId, setSelectedPortionId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [uploadingSketch, setUploadingSketch] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);

  const portions = plot?.portions || [];
  const isSplit = portions.length > 1;
  const isAdmin = user?.role === "admin" || user?.role === "super_admin";
  const isSuperAdmin = user?.role === "super_admin";
  const meta = plot ? (STATUS_META[plot.status] || STATUS_META.unknown) : null;

  // Only APPROVED vacant portions with no active booking can be booked.
  const bookablePortions = useMemo(
    () => portions.filter((p) =>
      p.status === "fully_vacant"
      && (p.approval_status || "approved") === "approved"
      && !p.active_booking
    ),
    [portions]
  );

  const selectedPortion = useMemo(
    () => portions.find((p) => p.id === selectedPortionId) || null,
    [portions, selectedPortionId]
  );

  const canBook = ["fully_vacant", "partially_vacant"].includes(plot?.status || "") && bookablePortions.length > 0;

  const reset = () => {
    setMode("view");
    setSelectedPortionId(null);
    setForm(EMPTY_FORM);
  };

  const handleClose = () => { reset(); onClose?.(); };

  const beginBooking = (portion) => {
    setSelectedPortionId(portion.id);
    setForm({
      ...EMPTY_FORM,
      area_sqyd: portion.area_sqyd ? String(portion.area_sqyd) : "",
    });
    setMode("book");
  };

  const beginBookingSingle = () => {
    if (bookablePortions.length === 1) {
      beginBooking(bookablePortions[0]);
    }
  };

  const uploadSketch = async (file) => {
    if (!file) return;
    setUploadingSketch(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads/booking-sketch", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, sketch_url: data.url }));
      toast.success("Sketch uploaded");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUploadingSketch(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!selectedPortion) {
      toast.error("Select a portion to book");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        plot_id: plot.id,
        portion_id: selectedPortion.id,
        // Always send the property/boundary details — required by backend for all bookings.
        direction: selectedPortion.direction,
        area_sqyd: parseFloat(form.area_sqyd),
        boundaries: form.boundaries,
        sketch_url: form.sketch_url,
        customer_name: form.customer_name,
        customer_mobile: form.customer_mobile,
        customer_email: form.customer_email || null,
        customer_address: form.customer_address || null,
        id_reference: form.id_reference || null,
        quoted_price: form.quoted_price ? parseFloat(form.quoted_price) : null,
        payment_mode: form.payment_mode || null,
        amount_paid: form.amount_paid ? parseFloat(form.amount_paid) : null,
        notes: form.notes || null,
      };
      if (isSplit) {
        // Partial plot flag is derived server-side; no additional payload needed here.
      }
      await api.post("/bookings", payload);
      toast.success("Booking request submitted — awaiting admin approval");
      onBooked?.();
    } catch (e2) {
      toast.error(formatApiError(e2));
    } finally {
      setSubmitting(false);
    }
  };

  if (!plot) return null;

  return (
    <Dialog open={!!plot} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="max-w-3xl rounded-sm max-h-[92vh] overflow-y-auto w-[calc(100vw-1.5rem)] sm:w-full" data-testid="plot-detail-dialog">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#C06C47] font-semibold">{plot.phase_name} · {plot.lp_no}</div>
          </div>
          <DialogTitle className="font-heading text-2xl sm:text-3xl flex flex-wrap items-center gap-2 sm:gap-3">
            Plot #{plot.plot_no}
            <span
              className="text-xs px-2.5 py-1 border font-medium"
              style={{ background: `${meta.bg}20`, borderColor: meta.color, color: meta.color }}
              data-testid="plot-status-chip"
            >
              {statusLabel(plot.status)}
            </span>
            {isSplit && (
              <span
                className="text-[10px] px-2 py-0.5 border font-medium tracking-wider uppercase"
                style={{ background: "#F4E6D9", borderColor: "#C06C47", color: "#8B4A2B" }}
                data-testid="plot-split-badge"
              >
                Split · {portions.length} portions
              </span>
            )}
            {plot.has_pending_portion && (
              <span
                className="text-[10px] px-2 py-0.5 border font-medium tracking-wider uppercase"
                style={{ background: "#EAB30820", borderColor: "#B08B00", color: "#B08B00" }}
                data-testid="plot-pending-portion-badge"
              >
                Pending SA Approval
              </span>
            )}
          </DialogTitle>
          <DialogDescription className="sr-only">
            Plot {plot.plot_no} details, portions, and booking actions.
          </DialogDescription>
        </DialogHeader>

        {mode === "view" && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
              <Field label="Total Area" value={formatSqYd(plot.total_sqyd)} />
              <Field label="Vacant Area" value={formatSqYd(plot.vacant_sqyd)} />
              <Field label="EMP Eligible" value={plot.emp_eligible ? "Yes" : "No"} />
              <Field label="Mortgage" value={plot.mortgage_flag ? "Flagged" : "No"} accent={plot.mortgage_flag ? "#C06C47" : undefined} />
              <Field label="Register Status" value={plot.status_raw || "—"} />
              <Field label="Portions" value={`${portions.length}`} />
            </div>

            {portions.length > 0 && (
              <div className="border border-[#E5E2DC] bg-[#F9F8F6]">
                <div className="px-3 py-2 border-b border-[#E5E2DC] flex items-center justify-between">
                  <div className="text-[10px] tracking-[0.2em] uppercase text-[#5C6653] font-semibold">Plot Portions (DTCP #{plot.plot_no})</div>
                  {isAdmin && !isSplit && (
                    <Button
                      type="button" variant="outline" size="sm"
                      className="rounded-sm text-xs h-7"
                      onClick={() => setMode("split")}
                      data-testid="split-plot-btn"
                    >
                      <Split className="h-3.5 w-3.5 mr-1.5" /> Split into portions
                    </Button>
                  )}
                </div>
                <div className="divide-y divide-[#E5E2DC]">
                  {portions.map((p) => {
                    const pMeta = STATUS_META[p.status] || STATUS_META.unknown;
                    const approval = p.approval_status || "approved";
                    const aMeta = APPROVAL_META[approval];
                    const bookable = p.status === "fully_vacant" && approval === "approved" && !p.active_booking;
                    return (
                      <div key={p.id} className="px-3 py-2.5 flex items-center gap-3 text-sm" data-testid={`portion-row-${p.direction}`}>
                        <div className="flex-1 min-w-0">
                          <div className="font-mono-anu text-xs text-[#5C6653]">{p.portion_code}</div>
                          <div className="font-medium">{DIRECTION_LABEL[p.direction] || p.direction} · {formatSqYd(p.area_sqyd)}</div>
                          {p.active_booking && (
                            <div className="text-[10px] text-[#2C5E7A] mt-0.5">Booking #{p.active_booking.booking_no} ({p.active_booking.status})</div>
                          )}
                          {approval === "rejected" && p.rejected_reason && (
                            <div className="text-[10px] text-[#DC2626] mt-0.5" data-testid={`portion-rejected-reason-${p.direction}`}>Rejected: {p.rejected_reason}</div>
                          )}
                        </div>
                        {approval !== "approved" && aMeta && (
                          <span
                            className="text-[10px] px-2 py-0.5 border font-medium tracking-wider uppercase whitespace-nowrap"
                            style={{ background: `${aMeta.bg}20`, borderColor: aMeta.color, color: aMeta.color }}
                            data-testid={`portion-approval-badge-${p.direction}`}
                          >
                            {aMeta.label}
                          </span>
                        )}
                        <span className="text-xs px-2 py-0.5 border font-medium" style={{ background: `${pMeta.bg}20`, borderColor: pMeta.color, color: pMeta.color }}>
                          {statusLabel(p.status)}
                        </span>
                        {bookable && (
                          <Button
                            type="button" size="sm"
                            className="rounded-sm bg-[#234B36] hover:bg-[#1A3A29] text-white h-7 text-xs"
                            onClick={() => beginBooking(p)}
                            data-testid={`book-portion-${p.direction}-btn`}
                          >
                            Book
                          </Button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {plot.remarks && (
              <div className="border border-[#E5E2DC] bg-[#F9F8F6] p-3 text-xs text-[#5C6653]">
                <div className="font-medium mb-1 text-[#1A1F16]">Remarks</div>
                {plot.remarks}
              </div>
            )}

            {plot.active_booking && (
              <div
                className={`border p-3 flex gap-3 ${plot.active_booking.stale_flag ? "border-[#DC2626]/40 bg-[#DC2626]/5" : "border-[#2C5E7A]/40 bg-[#2C5E7A]/5"}`}
                data-testid={plot.active_booking.stale_flag ? "plot-stale-booking-alert" : "plot-active-booking-alert"}
              >
                <AlertTriangle className={`h-5 w-5 flex-shrink-0 ${plot.active_booking.stale_flag ? "text-[#DC2626]" : "text-[#2C5E7A]"}`} />
                <div className="text-sm">
                  <div className={`font-medium ${plot.active_booking.stale_flag ? "text-[#DC2626]" : "text-[#2C5E7A]"}`}>
                    {plot.active_booking.stale_flag ? "Stale booking (needs review)" : "Active booking exists"}
                  </div>
                  <div className="text-xs text-[#5C6653] mt-1">
                    #{plot.active_booking.booking_no} · {plot.active_booking.status} · by {plot.active_booking.requested_by_name}
                  </div>
                  {plot.active_booking.stale_flag && plot.active_booking.stale_reason && (
                    <div className="text-[11px] text-[#DC2626]/80 mt-1 italic">
                      {plot.active_booking.stale_reason}
                    </div>
                  )}
                </div>
              </div>
            )}

            <DialogFooter className="pt-3 gap-2">
              <Button variant="outline" onClick={handleClose} data-testid="plot-close-btn" className="rounded-sm w-full sm:w-auto">Close</Button>
              {canBook && !isSplit && (
                <Button
                  onClick={beginBookingSingle}
                  data-testid="book-now-btn"
                  className="rounded-sm bg-[#234B36] hover:bg-[#1A3A29] text-white w-full sm:w-auto"
                >
                  Book this plot
                </Button>
              )}
            </DialogFooter>
          </div>
        )}

        {mode === "split" && (
          <SplitPlotForm
            plot={plot}
            onCancel={() => setMode("view")}
            onSaved={() => { onBooked?.(); }}
          />
        )}

        {mode === "book" && selectedPortion && (
          <form onSubmit={submit} className="space-y-4" data-testid="booking-form">
            <div className="border border-[#347355]/40 bg-[#347355]/5 p-3 text-xs text-[#234B36] flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-[#347355]" />
              <div>
                Booking <span className="font-mono-anu font-semibold">{selectedPortion.portion_code}</span> —
                {" "}{DIRECTION_LABEL[selectedPortion.direction]} · {formatSqYd(selectedPortion.area_sqyd)}.
                Awaiting admin approval after submission.
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <FormField label="Customer Name" required>
                <Input required value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} data-testid="booking-customer-name" className="rounded-sm" />
              </FormField>
              <FormField label="Mobile" required>
                <Input required value={form.customer_mobile} onChange={(e) => setForm({ ...form, customer_mobile: e.target.value })} data-testid="booking-customer-mobile" className="rounded-sm" />
              </FormField>
              <FormField label="Email">
                <Input type="email" value={form.customer_email} onChange={(e) => setForm({ ...form, customer_email: e.target.value })} data-testid="booking-customer-email" className="rounded-sm" />
              </FormField>
              <FormField label="ID Reference (Aadhaar/PAN)">
                <Input value={form.id_reference} onChange={(e) => setForm({ ...form, id_reference: e.target.value })} data-testid="booking-id-ref" className="rounded-sm" />
              </FormField>
              <FormField label="Quoted Price (₹)">
                <Input type="number" value={form.quoted_price} onChange={(e) => setForm({ ...form, quoted_price: e.target.value })} data-testid="booking-quoted-price" className="rounded-sm" />
              </FormField>
              <FormField label="Amount Paid (₹)">
                <Input type="number" value={form.amount_paid} onChange={(e) => setForm({ ...form, amount_paid: e.target.value })} data-testid="booking-amount-paid" className="rounded-sm" />
              </FormField>
              <FormField label="Payment Mode">
                <Input value={form.payment_mode} onChange={(e) => setForm({ ...form, payment_mode: e.target.value })} data-testid="booking-payment-mode" className="rounded-sm" />
              </FormField>
              <FormField label="Address" full>
                <Textarea value={form.customer_address} onChange={(e) => setForm({ ...form, customer_address: e.target.value })} data-testid="booking-address" rows={2} className="rounded-sm" />
              </FormField>
              <FormField label="Notes" full>
                <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="booking-notes" rows={2} className="rounded-sm" />
              </FormField>
            </div>

            {/* Property / boundary details section — required for EVERY booking
                (not just partial). CitySquare business rule enforced by backend. */}
            <div className="border border-[#C06C47]/30 bg-[#C06C47]/5 p-3 space-y-3">
                <div className="text-[10px] tracking-[0.2em] uppercase font-semibold text-[#8B4A2B]">
                  {isSplit ? "Partial-Plot Details (required by DTCP guidelines)" : "Plot Details & Boundaries (required)"}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <FormField label={isSplit ? "Area for this portion (sq.yd)" : "Booking area (sq.yd)"} required>
                    <Input
                      type="number" required step="0.01"
                      value={form.area_sqyd}
                      onChange={(e) => setForm({ ...form, area_sqyd: e.target.value })}
                      data-testid="booking-area-sqyd"
                      className="rounded-sm"
                    />
                  </FormField>
                  <FormField label="Direction">
                    <Input readOnly value={DIRECTION_LABEL[selectedPortion.direction] || selectedPortion.direction} className="rounded-sm bg-[#F9F8F6]" data-testid="booking-direction" />
                  </FormField>
                  {["north", "south", "east", "west"].map((side) => (
                    <FormField key={side} label={`Boundary — ${side.charAt(0).toUpperCase()}${side.slice(1)}`} required>
                      <Input
                        required
                        placeholder={`Adjacent to (e.g. Plot #${plot.plot_no + 1}, Road, Nala…)`}
                        value={form.boundaries[side]}
                        onChange={(e) => setForm({ ...form, boundaries: { ...form.boundaries, [side]: e.target.value } })}
                        data-testid={`booking-boundary-${side}`}
                        className="rounded-sm"
                      />
                    </FormField>
                  ))}
                </div>
                <div>
                  <Label className="text-xs font-medium mb-1 block">
                    Survey sketch <span className="text-[#C06C47]">*</span>
                    <span className="text-[10px] font-normal text-[#5C6653] ml-2">(JPG/PNG/PDF, ≤ 8 MB)</span>
                  </Label>
                  {form.sketch_url ? (
                    <div className="flex items-center gap-2 border border-[#E5E2DC] bg-white px-3 py-2 text-xs rounded-sm">
                      <CheckCircle2 className="h-4 w-4 text-[#347355]" />
                      <a href={form.sketch_url} target="_blank" rel="noreferrer" className="text-[#234B36] underline truncate flex-1" data-testid="booking-sketch-link">
                        Uploaded sketch
                      </a>
                      <button
                        type="button"
                        onClick={() => setForm({ ...form, sketch_url: "" })}
                        data-testid="booking-sketch-clear"
                        className="text-[#5C6653] hover:text-[#C06C47]"
                        aria-label="Remove sketch"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ) : (
                    <label
                      className="flex items-center gap-2 border border-dashed border-[#C06C47]/50 bg-white px-3 py-2.5 text-xs rounded-sm cursor-pointer hover:bg-[#F9F8F6]"
                      data-testid="booking-sketch-dropzone"
                    >
                      <Upload className="h-4 w-4 text-[#C06C47]" />
                      <span className="text-[#5C6653]">{uploadingSketch ? "Uploading…" : "Click to upload sketch"}</span>
                      <input
                        type="file" accept="image/*,application/pdf" className="hidden"
                        onChange={(e) => uploadSketch(e.target.files?.[0])}
                        data-testid="booking-sketch-input"
                      />
                    </label>
                  )}
                </div>
              </div>

            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={reset} data-testid="booking-cancel-btn" className="rounded-sm w-full sm:w-auto">Back</Button>
              <Button type="submit" disabled={submitting} data-testid="booking-submit-btn" className="rounded-sm bg-[#234B36] hover:bg-[#1A3A29] text-white w-full sm:w-auto">
                {submitting ? "Submitting…" : "Submit booking"}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ------------------------------------------------------------------
// Sub-component: Split Plot form (Admin / Super Admin)
// ------------------------------------------------------------------
const EMPTY_SPLIT_ROW = { direction: "N", area_sqyd: "", status: "fully_vacant" };

// Minimum saleable partial portion (business rule).
const MIN_PORTION_SQYD = 120;

function SplitPlotForm({ plot, onCancel, onSaved }) {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "super_admin";
  const [reason, setReason] = useState("");
  const [rows, setRows] = useState([
    { direction: "N", area_sqyd: plot.total_sqyd ? String(Math.round((plot.total_sqyd / 2) * 100) / 100) : "", status: "fully_vacant" },
    { direction: "S", area_sqyd: plot.total_sqyd ? String(Math.round((plot.total_sqyd / 2) * 100) / 100) : "", status: "fully_vacant" },
  ]);
  const [submitting, setSubmitting] = useState(false);

  const updateRow = (idx, patch) => {
    setRows((r) => r.map((row, i) => (i === idx ? { ...row, ...patch } : row)));
  };
  const removeRow = (idx) => setRows((r) => r.filter((_, i) => i !== idx));
  const addRow = () => {
    const used = new Set(rows.map((r) => r.direction));
    const next = ["N", "S", "E", "W", "NE", "NW", "SE", "SW", "P1", "P2", "P3", "P4"].find((d) => !used.has(d)) || "P3";
    setRows((r) => [...r, { direction: next, area_sqyd: "", status: "fully_vacant" }]);
  };

  // Client-side pre-validation matches the backend rules (min 120 + remainder ≥ 120 unless zero).
  const totalEntered = rows.reduce((s, r) => s + (parseFloat(r.area_sqyd) || 0), 0);
  const totalArea = Number(plot.total_sqyd || 0);
  const overshoot = totalArea > 0 && totalEntered > totalArea * 1.05;
  const undersized = rows
    .map((r, i) => ({ i, area: parseFloat(r.area_sqyd) || 0 }))
    .filter((r) => r.area > 0 && r.area < MIN_PORTION_SQYD);
  const remainder = totalArea > 0 ? totalArea - totalEntered : 0;
  const remainderTooSmall = totalArea > 0 && remainder > 0.5 && remainder < MIN_PORTION_SQYD;
  const clientError = overshoot
    ? `Total portion area (${totalEntered.toFixed(1)}) exceeds plot area (${totalArea.toFixed(1)}).`
    : undersized.length
      ? `Minimum partial plot booking size is ${MIN_PORTION_SQYD} sq. yards. Portion #${undersized[0].i + 1} is ${undersized[0].area.toFixed(1)} sq.yd.`
      : remainderTooSmall
        ? `Remaining area after split (${remainder.toFixed(1)} sq.yd) is below the ${MIN_PORTION_SQYD} sq.yd minimum. Adjust so nothing is leftover under ${MIN_PORTION_SQYD}.`
        : null;

  const submit = async (e) => {
    e.preventDefault();
    if (clientError) {
      toast.error(clientError);
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        reason: reason || null,
        portions: rows.map((r) => ({
          direction: r.direction,
          area_sqyd: parseFloat(r.area_sqyd),
          status: r.status,
        })),
      };
      const { data } = await api.post(`/plots/${plot.id}/split`, payload);
      toast.success(data?.message || "Plot split into portions");
      onSaved?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="split-plot-form">
      <div className="border border-[#C06C47]/30 bg-[#C06C47]/5 p-3 text-xs text-[#8B4A2B]">
        <div className="font-semibold mb-1">Split Plot #{plot.plot_no} into portions</div>
        The official DTCP plot number is preserved. Internal portion codes like
        <span className="font-mono-anu"> PH{plot.phase_no}-LP{(plot.lp_no||"").split("/")[0]}-P{plot.plot_no}-N</span>
        {" "}will be generated per direction. Total area: <span className="font-mono-anu">{formatSqYd(plot.total_sqyd)}</span>.
        <div className="mt-1.5 text-[11px]">
          <strong>Rule:</strong> minimum {MIN_PORTION_SQYD} sq. yards per portion, and any remaining slice must also be ≥ {MIN_PORTION_SQYD} sq. yards (unless exactly zero).
        </div>
        {!isSuperAdmin && (
          <div className="mt-2 border-l-2 border-[#B08B00] pl-2 text-[11px] text-[#8B4A2B]" data-testid="split-approval-note">
            <strong>Note:</strong> This partial portion will become bookable only after Super Admin approval.
          </div>
        )}
      </div>

      <div className="space-y-2">
        {rows.map((r, i) => (
          <div key={i} className="grid grid-cols-12 gap-2 items-end" data-testid={`split-row-${i}`}>
            <div className="col-span-4">
              <Label className="text-[10px] tracking-[0.2em] uppercase text-[#5C6653] font-semibold mb-1 block">Direction</Label>
              <select
                className="w-full h-9 border border-[#E5E2DC] rounded-sm px-2 text-sm bg-white"
                value={r.direction}
                onChange={(e) => updateRow(i, { direction: e.target.value })}
                data-testid={`split-direction-${i}`}
              >
                {["N", "S", "E", "W", "NE", "NW", "SE", "SW", "P1", "P2", "P3", "P4"].map((d) => (
                  <option key={d} value={d}>{DIRECTION_LABEL[d] || d}</option>
                ))}
              </select>
            </div>
            <div className="col-span-4">
              <Label className="text-[10px] tracking-[0.2em] uppercase text-[#5C6653] font-semibold mb-1 block">Area (sq.yd)</Label>
              <Input
                required type="number" step="0.01"
                value={r.area_sqyd}
                onChange={(e) => updateRow(i, { area_sqyd: e.target.value })}
                data-testid={`split-area-${i}`}
                className="rounded-sm h-9"
              />
            </div>
            <div className="col-span-3">
              <Label className="text-[10px] tracking-[0.2em] uppercase text-[#5C6653] font-semibold mb-1 block">Status</Label>
              <select
                className="w-full h-9 border border-[#E5E2DC] rounded-sm px-2 text-sm bg-white"
                value={r.status}
                onChange={(e) => updateRow(i, { status: e.target.value })}
                data-testid={`split-status-${i}`}
              >
                <option value="fully_vacant">Available</option>
                <option value="sold">Sold</option>
                <option value="booked">Booked</option>
              </select>
            </div>
            <div className="col-span-1 flex justify-end">
              {rows.length > 2 && (
                <button type="button" onClick={() => removeRow(i)} data-testid={`split-remove-${i}`}
                  className="h-9 w-9 text-[#C06C47] hover:bg-[#C06C47]/10 rounded-sm flex items-center justify-center"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
        ))}
        <div className="flex justify-between items-center pt-1">
          <Button type="button" variant="outline" size="sm" onClick={addRow} className="rounded-sm text-xs" data-testid="split-add-row">
            + Add portion
          </Button>
          <div className={`text-xs ${overshoot || undersized.length || remainderTooSmall ? "text-[#C06C47] font-semibold" : "text-[#5C6653]"}`} data-testid="split-total-indicator">
            Entered: <span className="font-mono-anu">{totalEntered.toFixed(1)}</span> / {totalArea.toFixed(1)} sq.yd
          </div>
        </div>
        {clientError && (
          <div className="text-xs text-[#DC2626] bg-[#DC2626]/5 border border-[#DC2626]/30 px-3 py-2 rounded-sm" data-testid="split-validation-error">
            {clientError}
          </div>
        )}
      </div>

      <FormField label="Reason (optional)">
        <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="split-reason" className="rounded-sm" />
      </FormField>

      <DialogFooter className="gap-2">
        <Button type="button" variant="outline" onClick={onCancel} data-testid="split-cancel-btn" className="rounded-sm w-full sm:w-auto">Cancel</Button>
        <Button type="submit" disabled={submitting} data-testid="split-submit-btn" className="rounded-sm bg-[#234B36] hover:bg-[#1A3A29] text-white w-full sm:w-auto">
          {submitting ? "Saving…" : "Split plot"}
        </Button>
      </DialogFooter>
    </form>
  );
}

function Field({ label, value, accent }) {
  return (
    <div>
      <div className="text-[10px] tracking-[0.2em] uppercase text-[#5C6653] font-semibold mb-1">{label}</div>
      <div className="text-sm font-medium" style={{ color: accent || "#1A1F16" }}>{value}</div>
    </div>
  );
}

function FormField({ label, required, full, children }) {
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <Label className="text-xs font-medium mb-1 block">{label}{required && <span className="text-[#C06C47]"> *</span>}</Label>
      {children}
    </div>
  );
}
